// The ops admin's day, fetched ONCE and shared.
//
// Before this, three different components each fetched /admin/ops/day/ on
// every screen (the flow strip, the counter page and the expenses page), so
// the same numbers arrived three times and could disagree mid-render. Now the
// layout owns the feed: nav badges, the home queues and the money line all
// read the same snapshot.
//
// Two calls make up "the day":
//   day      — what has happened and what is waiting (sales, dispatch, cash)
//   payables — what still has to go OUT today (refunds, vendors, chits)

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import expensesAxiosInstance from '../api/expensesAxiosInstance';
import { API_ENDPOINTS } from '../config/api.config';

const REFRESH_MS = 60_000;

const OpsDayContext = createContext({
  day: null,
  payables: null,
  loading: false,
  error: null,
  refresh: () => {},
});

export const OpsDayProvider = ({ children }) => {
  const [day, setDay] = useState(null);
  const [payables, setPayables] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const alive = useRef(true);
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    // Settled, not all: a failing payables call must not blank out the day.
    const [dayRes, payRes] = await Promise.allSettled([
      expensesAxiosInstance.get(API_ENDPOINTS.OPS.DAY),
      expensesAxiosInstance.get(API_ENDPOINTS.EXPENSES.PAYABLES, { params: { period: 'day' } }),
    ]);
    if (alive.current) {
      if (dayRes.status === 'fulfilled') {
        setDay(dayRes.value.data);
        setError(null);
      } else {
        setError(dayRes.reason?.response?.data?.error || 'Could not load today');
      }
      if (payRes.status === 'fulfilled') setPayables(payRes.value.data);
      setLoading(false);
    }
    inFlight.current = false;
  }, []);

  useEffect(() => {
    alive.current = true;
    refresh();
    const timer = setInterval(refresh, REFRESH_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive.current = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  const value = React.useMemo(
    () => ({ day, payables, loading, error, refresh }),
    [day, payables, loading, error, refresh],
  );
  return <OpsDayContext.Provider value={value}>{children}</OpsDayContext.Provider>;
};

export const useOpsDay = () => useContext(OpsDayContext);

// ─── What the day means ───────────────────────────────────────────────────────
// One place that turns the raw payload into the counts the whole workspace
// shows, so the nav badge and the home card can never say different things.
export const opsCounts = (day, payables) => {
  const sales = day?.sales || {};
  const counter = day?.counter || {};
  const dispatch = day?.dispatch || {};
  const refunds = payables?.sources?.returns || {};
  const vendors = payables?.sources?.osm || {};
  const commitments = payables?.sources?.money || {};
  return {
    toEnter: Number(day?.supervisor_sales?.pending_count || 0),
    toApprove: Number(sales.hold_count || 0) + Number(counter.to_confirm || 0),
    toDispatch: Number(dispatch.ready_count || 0),
    refundsDue: Number(refunds.outstanding_count || 0),
    refundsValue: Number(refunds.outstanding_total || 0),
    vendorsDue: Number(vendors.outstanding_count || 0),
    vendorsValue: Number(vendors.outstanding_total || 0),
    dueToday: Number(payables?.count || 0),
    dueTodayValue: Number(payables?.total || 0),
    overdue: Number(payables?.overdue_count || 0),
    overdueValue: Number(payables?.overdue_total || 0),
    commitmentsDue: Number(commitments.count || 0),
  };
};

export default useOpsDay;
